"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Mail } from "lucide-react";

export default function ZeroFeesStatementScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#00A876" }}>
            {t("End of month", "Fin de mes")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal max-w-[800px] mx-auto">
            {t("The line you ", "La línea que ")}
            <span className="text-redy-charcoal/30">{t("look forward to.", "esperas con ganas.")}</span>
          </h2>
        </motion.div>

        {/* Email mockup */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="max-w-[640px] mx-auto"
        >
          <div className="bg-[#FAFAF8] rounded-[32px] border border-black/5 shadow-[0_30px_80px_rgba(0,0,0,0.06)] overflow-hidden">
            {/* Mail header */}
            <div className="px-6 py-4 border-b border-black/5 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-redy-orange flex items-center justify-center text-white font-black text-[13px]">
                  R
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="text-[14px] font-bold text-redy-charcoal">Redy</div>
                    <div className="text-[11px] font-medium text-redy-secondary/60">
                      {t("Nov 1, 2026", "1 Nov 2026")}
                    </div>
                  </div>
                  <div className="text-[12px] font-medium text-redy-secondary truncate">
                    {t("Sales report · example", "Reporte de ventas · ejemplo")}
                  </div>
                </div>
              </div>
            </div>

            {/* Subject + body */}
            <div className="px-6 py-6">
              <div className="text-[18px] font-black text-redy-charcoal mb-4">
                {t("Example: your October summary", "Ejemplo: tu resumen de octubre")}
              </div>

              <div className="text-[13px] font-medium text-redy-secondary leading-relaxed mb-5">
                {t("Your October numbers, from your sales report:", "Tus números de octubre, desde tu reporte de ventas:")}
              </div>

              {/* Stat lines */}
              <div className="space-y-2 mb-6">
                <div className="flex justify-between items-baseline text-[13px]">
                  <span className="text-redy-secondary">{t("Card transactions", "Transacciones con tarjeta")}</span>
                  <span className="text-redy-charcoal font-bold tabular-nums" style={{ fontFamily: "ui-monospace, monospace" }}>
                    1,247
                  </span>
                </div>
                <div className="flex justify-between items-baseline text-[13px]">
                  <span className="text-redy-secondary">{t("Card volume", "Volumen tarjeta")}</span>
                  <span className="text-redy-charcoal font-bold tabular-nums" style={{ fontFamily: "ui-monospace, monospace" }}>
                    $23,742.00
                  </span>
                </div>
                <div className="flex justify-between items-baseline text-[13px]">
                  <span className="text-redy-secondary">{t("Covered by card pricing", "Cubierto por el precio con tarjeta")}</span>
                  <span className="text-redy-charcoal font-bold tabular-nums" style={{ fontFamily: "ui-monospace, monospace" }}>
                    $617.24
                  </span>
                </div>
              </div>

              {/* Highlighted savings line */}
              <motion.div
                initial={{ backgroundColor: "rgba(0,200,150,0)" }}
                whileInView={{ backgroundColor: "rgba(0,200,150,0.10)" }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="rounded-[14px] px-4 py-4"
              >
                <div className="flex justify-between items-baseline">
                  <span className="text-[12px] font-black uppercase tracking-widest" style={{ color: "#00A876" }}>
                    {t("Card fees covered", "Fees de tarjeta cubiertos")}
                  </span>
                  <span className="text-[28px] font-black tabular-nums" style={{ color: "#00A876", fontFamily: "ui-monospace, monospace" }}>
                    $617.24
                  </span>
                </div>
                <div className="text-[11px] font-medium mt-1" style={{ color: "#00A876", opacity: 0.75 }}>
                  {t("at a typical 2.6% card rate (example)", "a una tasa típica de tarjeta de 2.6% (ejemplo)")}
                </div>
              </motion.div>

              <div className="text-[13px] font-medium text-redy-secondary leading-relaxed mt-6">
                {t(
                  "At this pace, that's about $7,406 a year that doesn't come out of your margin.",
                  "A este ritmo, son unos $7,406 al año que no salen de tu margen."
                )}
              </div>

              <div className="mt-6 inline-flex items-center gap-2 text-[12px] font-bold text-redy-secondary/60">
                <Mail className="w-3.5 h-3.5" />
                {t("Illustrative example. Your real numbers live in your Redy reports.", "Ejemplo ilustrativo. Tus números reales están en tus reportes de Redy.")}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
